/**
 * WorkOrderPartyDialog — set/replace the party for one work order role
 * (epic #763, #768)
 *
 * The party type (person vs company) is constrained by the role: company-only
 * roles only let you pick a company, person-only roles only a person, and the
 * flexible roles (orderer, billing_party) offer a toggle. Quick-create flows
 * reuse the person/company form dialogs.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { describeApiError } from '@/lib/describeApiError';
import {
  Autocomplete,
  Box,
  Button,
  DialogContent,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { FormDialog } from '@/components/common/FormDialog';
import { PersonFormDialog } from '@/components/persons/PersonFormDialog';
import { CompanyFormDialog } from '@/components/companies/CompanyFormDialog';
import { usePersons, type Person } from '@/hooks/usePersons';
import { useCompanies, type Company } from '@/hooks/useCompanies';
import { useDebounce } from '@/hooks/useDebounce';
import { useSetWorkOrderParty } from '@/hooks/useWorkOrderParties';
import { allowedPartyTypesForRole, type PartyType, type WorkOrderPartyRole } from '@/lib/workOrderPartyRoles';
import { useNotify } from '@/stores/notificationStore';

interface WorkOrderPartyDialogProps {
  open: boolean;
  workOrderId: string;
  role: WorkOrderPartyRole;
  onClose: () => void;
}

export function WorkOrderPartyDialog({ open, workOrderId, role, onClose }: WorkOrderPartyDialogProps) {
  const { t } = useTranslation('workorders');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();

  const allowed = allowedPartyTypesForRole(role);
  const [partyType, setPartyType] = useState<PartyType>(allowed[0] ?? 'person');
  const [selectedPerson, setSelectedPerson] = useState<Person | null>(null);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [personQuickOpen, setPersonQuickOpen] = useState(false);
  const [companyQuickOpen, setCompanyQuickOpen] = useState(false);

  const setParty = useSetWorkOrderParty();

  useEffect(() => {
    if (open) {
      setPartyType(allowed[0] ?? 'person');
      setSelectedPerson(null);
      setSelectedCompany(null);
      setSearch('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, role]);

  const enableSearch = open && debouncedSearch.length >= 2;
  const personsQuery = usePersons({ q: debouncedSearch || undefined }, { enabled: enableSearch && partyType === 'person' });
  const companiesQuery = useCompanies({ q: debouncedSearch || undefined }, { enabled: enableSearch && partyType === 'company' });
  const personOptions = personsQuery.data?.pages.flatMap((p) => p.data) ?? [];
  const companyOptions = companiesQuery.data?.pages.flatMap((p) => p.data) ?? [];

  const selected = partyType === 'person' ? selectedPerson : selectedCompany;

  const handleSubmit = async () => {
    if (!selected) return;
    try {
      await setParty.mutateAsync({
        workOrderId,
        role,
        data:
          partyType === 'person'
            ? { partyType: 'person', personId: selectedPerson?.id }
            : { partyType: 'company', companyId: selectedCompany?.id },
      });
      notify.success(t('parties.saved'), '');
      onClose();
    } catch (error) {
      notify.error(tc('error'), describeApiError(error, tc, { fallback: t('parties.saveFailed') }));
    }
  };

  return (
    <>
      <FormDialog
        open={open}
        onClose={onClose}
        title={t(`parties.roles.${role}`)}
        isDirty={!!selected}
        onSubmit={() => void handleSubmit()}
        isLoading={setParty.isPending}
        submitDisabled={!selected}
        submitLabel={tc('save')}
        maxWidth="sm"
      >
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {allowed.length > 1 && (
              <ToggleButtonGroup
                value={partyType}
                exclusive
                fullWidth
                size="small"
                onChange={(_e, value: PartyType | null) => {
                  if (value) {
                    setPartyType(value);
                    setSelectedPerson(null);
                    setSelectedCompany(null);
                    setSearch('');
                  }
                }}
              >
                <ToggleButton value="person">{t('parties.person')}</ToggleButton>
                <ToggleButton value="company">{t('parties.company')}</ToggleButton>
              </ToggleButtonGroup>
            )}

            {partyType === 'person' ? (
              <Autocomplete<Person>
                options={personOptions}
                loading={personsQuery.isLoading}
                value={selectedPerson}
                onChange={(_e, value) => setSelectedPerson(value)}
                inputValue={search}
                onInputChange={(_e, value) => setSearch(value)}
                getOptionLabel={(o) => `${o.firstName} ${o.lastName}`}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                filterOptions={(x) => x}
                renderInput={(params) => <TextField {...params} label={t('parties.selectPerson')} required />}
              />
            ) : (
              <Autocomplete<Company>
                options={companyOptions}
                loading={companiesQuery.isLoading}
                value={selectedCompany}
                onChange={(_e, value) => setSelectedCompany(value)}
                inputValue={search}
                onInputChange={(_e, value) => setSearch(value)}
                getOptionLabel={(o) => o.name}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                filterOptions={(x) => x}
                renderInput={(params) => <TextField {...params} label={t('parties.selectCompany')} required />}
              />
            )}

            <Box>
              {partyType === 'person' ? (
                <Button size="small" startIcon={<AddIcon />} onClick={() => setPersonQuickOpen(true)}>
                  {t('parties.quickCreatePerson')}
                </Button>
              ) : (
                <Button size="small" startIcon={<AddIcon />} onClick={() => setCompanyQuickOpen(true)}>
                  {t('parties.quickCreateCompany')}
                </Button>
              )}
            </Box>
          </Stack>
        </DialogContent>
      </FormDialog>

      <PersonFormDialog
        open={personQuickOpen}
        onClose={() => setPersonQuickOpen(false)}
        onCreated={(person) => {
          setSelectedPerson(person);
          setSearch(`${person.firstName} ${person.lastName}`);
        }}
      />
      <CompanyFormDialog
        open={companyQuickOpen}
        onClose={() => setCompanyQuickOpen(false)}
        onCreated={(company) => {
          setSelectedCompany(company);
          setSearch(company.name);
        }}
      />
    </>
  );
}
